/**
 * Return & Refund Policy template — renderer + editor + default data.
 */

import { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";

export const RETURN_REFUND_DEFAULT = {
  hero: {
    eyebrow: "Customer care · Policy information",
    headline: "Return & Refund Policy",
    hl_accent: "Refund",
    lead: "Your satisfaction is our priority. At IMPORTREND, we want your order to arrive exactly as expected. If you receive a damaged product or an incorrect item, we're here to help.",
    pills: ["✓ Damaged items", "✓ Wrong items", "⏱ Report within 24 hours"],
  },
  eligibility: {
    eyebrow: "Eligibility",
    headline: "When is a return or refund applicable?",
    body: "Requests are considered only for the following situations, subject to verification.",
    cards: [
      { icon: "📦", title: "Damaged Product", text: "If your product arrives visibly damaged or broken, you may raise a return or refund request. Include clear photos of the damage and packaging along with your order details.", variant: "peach" },
      { icon: "🔄", title: "Different Product", text: "If you receive a product that is different from what you ordered, you may raise a request. Include photos of the item received and your order details so we can verify and resolve your concern.", variant: "blue" },
    ],
  },
  deadline: {
    hours: "24",
    headline: "Raise your request within 24 hours",
    body: "Contact our support team within 24 hours of receiving your order with your order ID, a description of the issue, and clear photos. Requests raised after this period are generally not eligible under this policy, except where applicable law requires otherwise.",
  },
  process: {
    eyebrow: "Simple process",
    headline: "How to raise a request",
    body: "Follow these four steps to help us review your concern quickly.",
    steps: [
      { icon: "📧", title: "Share Details", desc: "Email us your order ID, a description of the issue, and clear photos of the product and packaging." },
      { icon: "🔍", title: "Our Team Reviews", desc: "We assess your request and may ask for additional information or evidence if needed." },
      { icon: "📋", title: "Return Instructions", desc: "If a return is required and approved, we will share instructions. Do not send items back without prior confirmation." },
      { icon: "✅", title: "Resolution", desc: "Once verification is complete, we process any applicable refund or replacement as per our policy and applicable law." },
    ],
  },
  additional: {
    eyebrow: "Please read",
    headline: "Additional information",
    body: "A few important points before submitting a request.",
    items: [
      { icon: "🚫", title: "No Change-of-Mind Returns", text: "Returns or refunds are not accepted if you simply changed your mind about a purchase, unless required by applicable law." },
      { icon: "📦", title: "Keep the Product & Packaging", text: "Please retain the product and all original packaging until we have confirmed the resolution of your request." },
      { icon: "🚚", title: "Return Shipping", text: "Do not send any item back without receiving explicit instructions from our team. Unauthorised returns may not be processed." },
      { icon: "💳", title: "Refund Processing", text: "Approved refunds are initiated after verification. The time for the refund to reflect in your account depends on your payment method and bank or payment provider." },
    ],
    notice: "Order cancellation is governed by a separate policy. This policy does not limit any rights you may have under applicable consumer protection law.",
  },
  support: {
    eyebrow: "We're here to help",
    headline: "Need support?",
    body: "If you have received a damaged or incorrect product, contact us within 24 hours with your order ID, description and photos. Office hours: Monday–Saturday, 09:00–18:00 IST.",
    btn1_text: "Email Support",
    btn1_href: "mailto:support@importrend.com",
    btn2_text: "7011608563",
    btn2_href: "tel:+917011608563",
  },
};

const RR_CSS = `
.rr-root{--ink:#33241b;--orange:#e07828;--muted:#6b5c52;--cream:#fff8f1;--line:#f0ddd0;--peach:#fff0e3;--blue:#e9f5ff;color:var(--ink);background:#fffdfb;font-family:Inter,ui-sans-serif,system-ui,-apple-system,"Segoe UI",Arial,sans-serif;line-height:1.55}
.rr-root *{box-sizing:border-box}
.rr-root h1,.rr-root h2,.rr-root h3,.rr-root p{margin-top:0}
.rr-root a{color:var(--orange)}
.rr-wrap{width:min(1180px,calc(100% - 44px));margin:auto}
.rr-eyebrow{font-size:.78rem;letter-spacing:.14em;text-transform:uppercase;font-weight:800;color:var(--orange);margin-bottom:12px;display:block}
.rr-orange{color:var(--orange)}
.rr-hero{background:radial-gradient(circle at 80% 15%,#ffd9b8 0,transparent 38%),linear-gradient(130deg,#fff9f3,#fff1e2);padding:72px 0 60px;overflow:hidden}
.rr-hero-grid{display:grid;grid-template-columns:1.15fr .85fr;align-items:center;gap:50px}
.rr-hero h1{font-size:clamp(2.4rem,4.5vw,4.4rem);line-height:1.06;letter-spacing:-.045em;margin-bottom:16px}
.rr-lead{font-size:1.1rem;color:var(--muted);max-width:560px;margin-bottom:24px}
.rr-pills{display:flex;gap:10px;flex-wrap:wrap}
.rr-pill{padding:9px 15px;border-radius:99px;background:#fff;border:1px solid #f4dcc6;font-size:.86rem;font-weight:700}
.rr-illustration{min-height:300px;border-radius:32px;background:linear-gradient(135deg,#ffd4a8,#ffe8cc);display:grid;place-items:center;position:relative;overflow:hidden;box-shadow:0 22px 58px #c07a3020}
.rr-illustration:before{content:"";position:absolute;width:220px;height:220px;border:30px solid rgba(255,255,255,.5);border-radius:50%;right:-70px;top:-80px}
.rr-ill-emoji{font-size:clamp(80px,14vw,130px);filter:drop-shadow(0 14px 10px #aa683b28);z-index:1}
.rr-section{padding:60px 0}
.rr-section.rr-soft{background:var(--cream)}
.rr-sh{text-align:center;max-width:720px;margin:0 auto 34px}
.rr-sh h2{font-size:clamp(1.6rem,2.8vw,2.6rem);letter-spacing:-.03em;margin-bottom:12px}
.rr-sh p{color:var(--muted)}
.rr-grid2{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:20px}
.rr-card{border-radius:22px;padding:28px;border:1px solid var(--line);box-shadow:0 8px 26px #6e3a0c08}
.rr-card p:last-child{margin-bottom:0}
.rr-card.rr-peach{background:var(--peach)}.rr-card.rr-blue{background:var(--blue)}
.rr-icon{width:52px;height:52px;border-radius:16px;background:#fff0e0;display:grid;place-items:center;font-size:1.55rem;margin-bottom:16px}
.rr-deadline{background:linear-gradient(130deg,#fff2e8,#ffe3cd);border-radius:28px;padding:44px;display:grid;grid-template-columns:auto 1fr;align-items:center;gap:40px;margin:0 0 24px}
.rr-clock{background:var(--orange);color:#fff;border-radius:22px;padding:24px 28px;text-align:center;min-width:120px}
.rr-clock .rr-num{font-size:3.5rem;font-weight:900;line-height:1}
.rr-clock .rr-unit{font-size:.82rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;opacity:.85}
.rr-deadline h2{font-size:clamp(1.4rem,2.5vw,2.2rem);margin-bottom:12px}
.rr-deadline p{color:var(--muted);margin-bottom:0}
.rr-steps4{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px}
.rr-step-card{border-radius:22px;background:#fff;padding:26px;border:1px solid var(--line);box-shadow:0 8px 26px #6e3a0c08}
.rr-step-num{width:36px;height:36px;border-radius:50%;background:var(--orange);color:#fff;font-weight:900;display:grid;place-items:center;margin-bottom:14px;font-size:.9rem}
.rr-step-card h3{font-size:1rem;margin-bottom:8px}
.rr-step-card p{font-size:.9rem;color:var(--muted);margin-bottom:0}
.rr-detail{display:flex;gap:16px;align-items:flex-start;border-radius:18px;padding:22px;background:#fff;border:1px solid var(--line)}
.rr-notice{margin-top:24px;background:#fff0df;border-left:4px solid var(--orange);border-radius:12px;padding:20px 22px;color:#6b4028}
.rr-cta{background:linear-gradient(120deg,#f5903a,#e67b25);border-radius:28px;padding:44px 48px;color:#fff;display:grid;grid-template-columns:1fr auto;align-items:center;gap:28px}
.rr-cta h2{color:#fff;font-size:clamp(1.5rem,2.5vw,2.2rem);margin-bottom:10px}
.rr-cta p{color:#fff5ec;margin-bottom:0}
.rr-btns{display:flex;gap:12px;flex-wrap:wrap}
.rr-btn{display:inline-block;padding:13px 22px;border-radius:12px;background:#fff;color:#a44a18;text-decoration:none;font-weight:800;white-space:nowrap;font-size:.93rem}
.rr-btn-out{background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.5);color:#fff}
@media(max-width:900px){.rr-hero-grid{grid-template-columns:1fr;gap:30px}.rr-steps4{grid-template-columns:repeat(2,1fr)}.rr-cta{grid-template-columns:1fr}}
@media(max-width:600px){.rr-wrap{width:calc(100% - 28px)}.rr-hero{padding:44px 0}.rr-grid2,.rr-steps4{grid-template-columns:1fr}.rr-deadline{grid-template-columns:1fr;gap:20px}.rr-section{padding:44px 0}.rr-cta{padding:28px 22px}}
`;

export function ReturnRefundRenderer({ data }) {
  const d  = data || RETURN_REFUND_DEFAULT;
  const h  = d.hero        || RETURN_REFUND_DEFAULT.hero;
  const el = d.eligibility || RETURN_REFUND_DEFAULT.eligibility;
  const dl = d.deadline    || RETURN_REFUND_DEFAULT.deadline;
  const pr = d.process     || RETURN_REFUND_DEFAULT.process;
  const ad = d.additional  || RETURN_REFUND_DEFAULT.additional;
  const s  = d.support     || RETURN_REFUND_DEFAULT.support;

  const accentify = (txt, acc) => {
    if (!acc || !txt.includes(acc)) return <span>{txt}</span>;
    const parts = txt.split(acc);
    return <span>{parts[0]}<span className="rr-orange">{acc}</span>{parts.slice(1).join(acc)}</span>;
  };

  return (
    <div className="rr-root">
      <style>{RR_CSS}</style>

      <section className="rr-hero">
        <div className="rr-wrap rr-hero-grid">
          <div>
            <span className="rr-eyebrow">{h.eyebrow}</span>
            <h1>{accentify(h.headline, h.hl_accent)}</h1>
            <p className="rr-lead">{h.lead}</p>
            <div className="rr-pills">
              {(h.pills||[]).map((p, i) => <span key={i} className="rr-pill">{p}</span>)}
            </div>
          </div>
          <div className="rr-illustration"><div className="rr-ill-emoji">📦</div></div>
        </div>
      </section>

      <section className="rr-section">
        <div className="rr-wrap">
          <div className="rr-sh">
            <span className="rr-eyebrow">{el.eyebrow}</span>
            <h2>{el.headline}</h2>
            <p>{el.body}</p>
          </div>
          <div className="rr-grid2">
            {(el.cards||[]).map((c, i) => (
              <article key={i} className={`rr-card rr-${c.variant||"peach"}`}>
                <div className="rr-icon">{c.icon}</div>
                <h3>{c.title}</h3>
                <p style={{color:"var(--muted)"}}>{c.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rr-section rr-soft">
        <div className="rr-wrap">
          <div className="rr-deadline">
            <div className="rr-clock">
              <div className="rr-num">{dl.hours}</div>
              <div className="rr-unit">HOURS</div>
            </div>
            <div>
              <h2>{dl.headline}</h2>
              <p>{dl.body}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="rr-section">
        <div className="rr-wrap">
          <div className="rr-sh">
            <span className="rr-eyebrow">{pr.eyebrow}</span>
            <h2>{pr.headline}</h2>
            <p>{pr.body}</p>
          </div>
          <div className="rr-steps4">
            {(pr.steps||[]).map((step, i) => (
              <article key={i} className="rr-step-card">
                <div className="rr-step-num">{i + 1}</div>
                <div className="rr-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rr-section rr-soft">
        <div className="rr-wrap">
          <div className="rr-sh">
            <span className="rr-eyebrow">{ad.eyebrow}</span>
            <h2>{ad.headline}</h2>
            <p>{ad.body}</p>
          </div>
          <div className="rr-grid2">
            {(ad.items||[]).map((item, i) => (
              <div key={i} className="rr-detail">
                <div className="rr-icon">{item.icon}</div>
                <div>
                  <h3 style={{marginBottom:"6px"}}>{item.title}</h3>
                  <p style={{color:"var(--muted)",marginBottom:0}}>{item.text}</p>
                </div>
              </div>
            ))}
          </div>
          {ad.notice && <div className="rr-notice">{ad.notice}</div>}
        </div>
      </section>

      <section className="rr-section">
        <div className="rr-wrap">
          <div className="rr-cta">
            <div>
              <span className="rr-eyebrow" style={{color:"#ffe8d4"}}>{s.eyebrow}</span>
              <h2>{s.headline}</h2>
              <p>{s.body}</p>
            </div>
            <div className="rr-btns">
              <a className="rr-btn" href={s.btn1_href}>{s.btn1_text}</a>
              <a className="rr-btn rr-btn-out" href={s.btn2_href}>{s.btn2_text}</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, multiline = false, rows = 3 }) {
  const cls = "w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white resize-none";
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {multiline
        ? <textarea rows={rows} value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />
        : <input type="text" value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />}
    </div>
  );
}

function SectionBlock({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-xs font-bold text-slate-700">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 py-4 space-y-3 bg-white">{children}</div>}
    </div>
  );
}

export function ReturnRefundEditor({ data, onChange }) {
  const d = data || RETURN_REFUND_DEFAULT;
  const set = (section, field, value) => onChange({ ...d, [section]: { ...(d[section] || {}), [field]: value } });
  const setArrItem = (section, arrKey, idx, field, value) => {
    const arr = [...(d[section]?.[arrKey] || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    onChange({ ...d, [section]: { ...(d[section] || {}), [arrKey]: arr } });
  };

  const h = d.hero || {};
  const el = d.eligibility || {};
  const dl = d.deadline || {};
  const pr = d.process || {};
  const ad = d.additional || {};
  const s = d.support || {};

  return (
    <div className="space-y-3">
      <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-xs text-orange-700">
        <strong>Return & Refund Policy Template</strong>
      </div>

      <SectionBlock title="1. Hero" defaultOpen>
        <Field label="Eyebrow" value={h.eyebrow} onChange={v => set("hero","eyebrow",v)} />
        <Field label="Headline" value={h.headline} onChange={v => set("hero","headline",v)} />
        <Field label="Accent word" value={h.hl_accent} onChange={v => set("hero","hl_accent",v)} />
        <Field label="Lead" value={h.lead} onChange={v => set("hero","lead",v)} multiline />
        <Field label="Pills (comma-separated)" value={(h.pills||[]).join(", ")} onChange={v => set("hero","pills",v.split(",").map(x=>x.trim()).filter(Boolean))} />
      </SectionBlock>

      <SectionBlock title="2. Eligibility Cards">
        <Field label="Eyebrow" value={el.eyebrow} onChange={v => set("eligibility","eyebrow",v)} />
        <Field label="Headline" value={el.headline} onChange={v => set("eligibility","headline",v)} />
        <Field label="Body" value={el.body} onChange={v => set("eligibility","body",v)} multiline />
        {(el.cards||[]).map((c,i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <div className="grid grid-cols-4 gap-2">
              <Field label="Icon" value={c.icon} onChange={v => setArrItem("eligibility","cards",i,"icon",v)} />
              <div className="col-span-3"><Field label="Title" value={c.title} onChange={v => setArrItem("eligibility","cards",i,"title",v)} /></div>
            </div>
            <Field label="Text" value={c.text} onChange={v => setArrItem("eligibility","cards",i,"text",v)} multiline />
          </div>
        ))}
      </SectionBlock>

      <SectionBlock title="3. 24-Hour Deadline">
        <Field label="Hours number" value={dl.hours} onChange={v => set("deadline","hours",v)} />
        <Field label="Headline" value={dl.headline} onChange={v => set("deadline","headline",v)} />
        <Field label="Body" value={dl.body} onChange={v => set("deadline","body",v)} multiline />
      </SectionBlock>

      <SectionBlock title="4. Process Steps (4 steps)">
        <Field label="Eyebrow" value={pr.eyebrow} onChange={v => set("process","eyebrow",v)} />
        <Field label="Headline" value={pr.headline} onChange={v => set("process","headline",v)} />
        <Field label="Body" value={pr.body} onChange={v => set("process","body",v)} multiline />
        {(pr.steps||[]).map((step,i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <div className="grid grid-cols-4 gap-2">
              <Field label="Icon" value={step.icon} onChange={v => setArrItem("process","steps",i,"icon",v)} />
              <div className="col-span-3"><Field label="Title" value={step.title} onChange={v => setArrItem("process","steps",i,"title",v)} /></div>
            </div>
            <Field label="Description" value={step.desc} onChange={v => setArrItem("process","steps",i,"desc",v)} multiline />
          </div>
        ))}
      </SectionBlock>

      <SectionBlock title="5. Additional Info">
        <Field label="Eyebrow" value={ad.eyebrow} onChange={v => set("additional","eyebrow",v)} />
        <Field label="Headline" value={ad.headline} onChange={v => set("additional","headline",v)} />
        <Field label="Body" value={ad.body} onChange={v => set("additional","body",v)} multiline />
        {(ad.items||[]).map((item,i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <div className="grid grid-cols-4 gap-2">
              <Field label="Icon" value={item.icon} onChange={v => setArrItem("additional","items",i,"icon",v)} />
              <div className="col-span-3"><Field label="Title" value={item.title} onChange={v => setArrItem("additional","items",i,"title",v)} /></div>
            </div>
            <Field label="Text" value={item.text} onChange={v => setArrItem("additional","items",i,"text",v)} multiline />
          </div>
        ))}
        <Field label="Notice / disclaimer" value={ad.notice} onChange={v => set("additional","notice",v)} multiline />
      </SectionBlock>

      <SectionBlock title="6. Support CTA">
        <Field label="Eyebrow" value={s.eyebrow} onChange={v => set("support","eyebrow",v)} />
        <Field label="Headline" value={s.headline} onChange={v => set("support","headline",v)} />
        <Field label="Body" value={s.body} onChange={v => set("support","body",v)} multiline />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Button 1 text" value={s.btn1_text} onChange={v => set("support","btn1_text",v)} />
          <Field label="Button 1 href" value={s.btn1_href} onChange={v => set("support","btn1_href",v)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Button 2 text" value={s.btn2_text} onChange={v => set("support","btn2_text",v)} />
          <Field label="Button 2 href" value={s.btn2_href} onChange={v => set("support","btn2_href",v)} />
        </div>
      </SectionBlock>
    </div>
  );
}
